import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { clearApolloCache } from '@/shared/api';
import { EMITTER_CONSTANTS, KEY_RECOIL, TAB_KEY } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { fetchHolterTasks, callStartHolterStudyProcessing } from '@/entities/study';
import { checkLimitStudyProcessing, isHolterProcessingAvailable } from '@/shared/lib/aiUtils';
import { zeroPad, auth } from '@/shared/lib';
import { reportTabStatus } from '@/shared/lib/devLog';

import { fetchAIProcessingStatus, selectIsActiveTab, useReportStore } from '@/entities/report';
import emitter from '@/shared/lib/eventEmitter';
import type { IReportInfo } from '@/shared/model';
import type { BeatsTabProps } from './index';

/**
 * Coordinates the beats tab lifecycle — kicking off Holter AI processing for the study and gating the tab while another tab's processing finishes. Tracks whether tab 1 is active, starts Holter study processing once a socket id exists, re-checks pending AI requests held in localStorage on activation, clears the Apollo cache and re-enables the tab when AI processing stops, and restarts processing after a socket reconnect.
 *
 * @param props - Beats tab props whose activeTab id decides when tab 1 becomes the active tab.
 * @returns An object with the tab store key, the AI processing and beat download flags, study and profile ids, the read-only and IndexedDB load states, whether the tab is currently usable, and a ref recording whether it has been visited.
 */
const useBeatsTab = ({ activeTab }: Pick<BeatsTabProps, 'activeTab'>) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const setLimitProcessingStudy = useReportStore((s) => s.actions.setLimitProcessingStudy);
  const setIsOpenProcessingLimit = useReportStore((s) => s.actions.setIsOpenProcessingLimit);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const isLoadedIndexDB = useReportStore((s) => s.isLoadedIndexDB);
  const isCompletedDownloadBeat = useReportStore((s) => s.isCompletedDownloadBeat);

  const isStartedAiProcessing = useReportStore((s) => s.isStartedAiProcessing);
  const setIsStartedAiProcessing = useReportStore((s) => s.actions.setIsStartedAiProcessing);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const setIsActiveTabAction = useReportStore((s) => s.actions.setIsActiveTab);
  /**
   * Marks the beats tab as active or inactive in the report store. Forwards the value to the shared setIsActiveTab action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setIsActiveTab = (v: Parameters<typeof setIsActiveTabAction>[1]) =>
    setIsActiveTabAction(keyRecoil, v);
  const [isTabActive, setIsTabActive] = useState(false);

  const willClearCacheRef = useRef<any>();
  const hasVisitTab = useRef(false);
  const hasCallStart = useRef<any>();

  // reportInfoState (Recoil/model.ts, Task 7) doesn't declare isNotEdit - pre-existing gap.
  const { studyId, profileId, isNotEdit, studyFid } = useReportStore(
    (s) => s.reportInfo,
  ) as IReportInfo & { isNotEdit?: any };

  /**
   * Requests server-side Holter AI processing for the current study and profile, surfacing any concurrency limit. Emits the pending profile list before and after the call, refreshes the Holter task list, opens the processing limit modal with the limit count and zero-padded study fids when the simultaneous study cap is hit, and otherwise flags AI processing as started once the result is ready.
   *
   * @returns A promise resolving once processing has been requested and the limit state updated.
   */
  const startHolterStudyProcessing = async () => {
    emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, [{ id: profileId }]);
    const result = await callStartHolterStudyProcessing(studyId, profileId);
    const holterTasks = await fetchHolterTasks();
    emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, holterTasks?.pendingProfiles || []);
    if (checkLimitStudyProcessing(result)) {
      setLimitProcessingStudy({
        isOpenModal: true,
        limitCount: result?.simultaneousStudyLimit,
        studyFids: _.map(result?.processingStudyFids, (x: any) => zeroPad(x)),
      });
      return;
    }
    hasCallStart.current = true;
    if (!isHolterProcessingAvailable(holterTasks, studyFid as any)) {
      setIsOpenProcessingLimit(true);
    }
    if (result?.isReady) {
      setIsStartedAiProcessing(true);
    }
  };

  /**
   * Waits for a live socket connection before requesting Holter AI processing. Reads the socket id from auth and reschedules itself every 500ms until one exists, then calls the start routine that emits pending profiles and handles the study limit modal.
   *
   * @returns Nothing.
   */
  const startHolterStudyProcessingByRetry = () => {
    const socketId = auth.getSocketId();
    console.log('[BeatHR] startHolterStudyProcessingByRetry:socketId', { socketId });
    if (!socketId) {
      setTimeout(startHolterStudyProcessingByRetry, 500);
      return;
    }
    startHolterStudyProcessing();
  };

  useEffect(() => {
    const isActive = activeTab === TAB_KEY.BEATS;
    setIsTabActive(false);
    setIsActiveTab(isActive);
    hasVisitTab.current = isActive;
  }, [activeTab]);

  useEffect(() => {
    reportTabStatus(keyRecoil, {
      isActiveTab,
      isStartedAiProcessing,
      hasCallStart: !!hasCallStart.current,
    });
    if (isActiveTab && !isStartedAiProcessing && !hasCallStart.current) {
      startHolterStudyProcessingByRetry();
    }
  }, [isActiveTab, isStartedAiProcessing]);

  /**
   * Decides whether the beats tab may be entered while AI processing requests started from other tabs are still outstanding. Reads the per-profile and per-study requestId list from localStorage, short-circuits to active when it is empty, owned by this tab, or the ai_process flag is set, otherwise polls the processing status of every pending request, prunes finished ones from storage, and activates the tab only if at least one completed, falling back to inactive on error.
   *
   * @returns A promise resolving once the tab active flag has been decided.
   */
  const checkCanSwitchTab = async () => {
    try {
      const keyStorage = `requestId-${profileId}-${studyFid}`;
      const storedResult = localStorage.getItem(keyStorage);
      const requestData = JSON.parse(storedResult);
      console.log(`BeatsTab isActiveTab: ${keyStorage}`, requestData);
      if (_.isEmpty(requestData)) return setIsTabActive(true);
      const { type, requestId } = requestData?.[0] || {};
      if (!requestId || type === keyRecoil) return setIsTabActive(true);
      const isProcessing = localStorage.getItem(`ai_process-${profileId}`);
      if (JSON.parse(isProcessing as any)) return setIsTabActive(true);
      const promisses: any[] = [];
      setIsTabActive(false);
      _.forEach(requestData, (x: any) => {
        if (x.requestId) {
          promisses.push(fetchAIProcessingStatus(x.requestId, 'switchTab'));
        }
      });
      const result = await Promise.all(promisses);
      let canSwitchTab = false;
      const updateSuccess: any[] = [];
      _.forEach(result, (x: any, i: any) => {
        if (x === true) {
          canSwitchTab = true;
          updateSuccess.push(requestData?.[i]);
        }
      });
      _.remove(
        requestData,
        (x: any) => !!_.find(updateSuccess, (y: any) => y.requestId === x.requestId),
      );
      localStorage.setItem(keyStorage, JSON.stringify(requestData));

      if (canSwitchTab) {
        localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(canSwitchTab));
      }

      setIsTabActive(canSwitchTab);
    } catch (error) {
      console.log(error);
      setIsTabActive(false);
    }
    return null;
  };

  useEffect(() => {
    if (isActiveTab) {
      checkCanSwitchTab();
    }
  }, [isActiveTab]);

  useEmitter(
    EMITTER_CONSTANTS.AI_STOP_PROCESSING,
    async (msg: any) => {
      await clearApolloCache();
      willClearCacheRef.current = true;
      if (isActiveTab && !_.isEmpty(msg)) {
        const { requestId } = msg || {};
        const keyStorage = `requestId-${profileId}-${studyFid}`;
        const storedResult = localStorage.getItem(keyStorage);
        const requestData = JSON.parse(storedResult as any);
        _.remove(requestData, (x: any) => x.requestId === requestId);
        localStorage.setItem(keyStorage, JSON.stringify(requestData));
        if (_.isEmpty(requestData)) {
          localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(true));
          setIsTabActive(true);
        }
      }
    },
    [isActiveTab, profileId, studyFid],
  );

  useEffect(() => {
    if (willClearCacheRef.current) {
      willClearCacheRef.current = false;
      emitter.emit(EMITTER_CONSTANTS.WILL_CLEAR_CACHE);
    }
  }, [isTabActive]);

  useEmitter(
    EMITTER_CONSTANTS.SOCKET_RECONNECTED,
    () => {
      if (isTabActive && !isStartedAiProcessing) {
        startHolterStudyProcessingByRetry();
      }
    },
    [isTabActive, isStartedAiProcessing],
  );

  return {
    keyRecoil,
    isStartedAiProcessing,
    isShowTabHeader,
    studyId,
    profileId,
    isNotEdit,
    isLoadedIndexDB,
    isCompletedDownloadBeat,
    isTabActive,
    hasVisitTab,
  };
};

export default useBeatsTab;
